// @flow
import _ from 'lodash';
import { useEffect, useState } from 'react';
// Types
import type { Blend } from '../../blend/types';
import type { LineItem } from '../../current-order/types';
// Components
import { ItemListImage } from '../../common';
// Helpers
import { quantityConverter, scaleAndFormatPrice } from '../../common';
//Connectors
import blendsListConnector from '../../blend/connectors/blendsListConnector';
// Style
import { fatLevels } from '../../current-order/constants/fatLevels';
import './LineItemList.css';

type Props = {
  items: LineItem[], // Ajustado para bater com a desestruturação do componente
  orderNumber: String,
  getBlends: (orderNumber: number) => void,
  blendsByOrderNumber: (orderNumber: number) => Blend[],
};

const mix = (blends, mixId) => {
  return _.filter(blends, function (o) {
    return o.id === mixId;
  }).pop();
};

const LineItemList = ({
  items = [],
  orderNumber,
  getBlends,
  blendsByOrderNumber,
}: Props) => {
  const [showObservation, setShowObservation] = useState(false);
  const hasLineItems = items.length > 0;

  useEffect(() => {
    if (orderNumber) {
      getBlends(orderNumber);
    }
    // eslint-disable-next-line
  }, [orderNumber]);

  const blends = blendsByOrderNumber(orderNumber);

  const handleToggleShowObservation = () => {
    setShowObservation(prevValue => !prevValue);
  };

  const lineItemPortioningMessage = item => {
    const portionNumber = Math.floor(item.quantity / item.portioningSize);
    let remainder = item.quantity - item.portioningSize * portionNumber;

    const unitForQuantityPortioning =
      item.portioningSize >= 1000 ? 'Kg' : 'gramas';
    const unitForQuantityRemainder = remainder >= 1000 ? 'Kg' : 'gramas';

    const portioningSizeValue =
      item.portioningSize >= 1000
        ? item.portioningSize / 1000
        : item.portioningSize;
    remainder = remainder >= 1000 ? remainder / 1000 : remainder;

    return (
      <div class="profile-item-portioning-info">
        {item.portioningSize && item.portioningSize !== item.quantity && (
          <>
            {portionNumber} embalagem de {portioningSizeValue}{' '}
            {unitForQuantityPortioning}
            {remainder > 0 && (
              <>
                {' '}
                + 1 embalagem de {remainder} {unitForQuantityRemainder}{' '}
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <section className="line-item-list-main">
      <h3 className="line-item-list-main-title">Produtos</h3>
      {hasLineItems &&
        items.map((lineItem: LineItem, index) => {
          const isGiftCard = !!lineItem.giftCard;

          const currentBlend = mix(blends, lineItem.mixId);
          const blendFatLevel = currentBlend?.fatLevel
            ? fatLevels[currentBlend.fatLevel] || currentBlend.fatLevel
            : null;

          return (
            <div className="line-item-list-item" key={index}>
              <ItemListImage variant={lineItem.variant} />
              <div className="line-item-list-column">
                <div className="line-item-list-item-content">
                  <span>{lineItem.variant.manufacturer}</span>
                  <h4>{lineItem.variant.name}</h4>
                  {!isGiftCard &&
                    mix(blends, lineItem.mixId) &&
                    ` - ${mix(blends, lineItem.mixId).name}`}
                </div>

                {isGiftCard ? (
                  <>
                    <div className="line-item-list-row">
                      <span className="line-item-list-row-text">Valor</span>
                      <span className="line-item-list-row-info gift-card-item-list-row-info">
                        R${' '}
                        {parseFloat(lineItem.giftCard.amount)
                          .toFixed(2)
                          .replace('.', ',')}
                      </span>
                    </div>
                    <div className="line-item-list-row">
                      <span className="line-item-list-row-text">
                        Destinatário{' '}
                      </span>
                      <span className="line-item-list-row-info gift-card-item-list-row-info">
                        {lineItem.giftCard.receiverName}
                      </span>
                    </div>
                    <div className="line-item-list-row">
                      <span className="line-item-list-row-text">E-mail</span>
                      <span className="line-item-list-row-info gift-card-item-list-row-info">
                        {lineItem.giftCard.email}
                      </span>
                    </div>
                    <div className="line-item-list-row">
                      <span className="line-item-list-row-text">
                        Data de envio
                      </span>
                      <span className="line-item-list-row-info gift-card-item-list-row-info">
                        {new Date(lineItem.giftCard.sendAt).toLocaleDateString(
                          'pt-BR',
                        )}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="line-item-list-row">
                      <span className="line-item-list-row-text">
                        Quantidade do pedido
                      </span>
                      <div className="line-item-list-row-info">
                        {quantityConverter(
                          lineItem.quantity,
                          lineItem.variant.unitForQuantity,
                        )}
                      </div>
                    </div>
                    <div className="line-item-list-row">
                      <span className="line-item-list-row-text">
                        Quantidade pesada
                      </span>
                      <div className="line-item-list-row-info">
                        {quantityConverter(
                          lineItem.producedQuantity,
                          lineItem.variant.unitForQuantity,
                        )}
                      </div>
                    </div>
                    <div className="line-item-list-row">
                      <span className="line-item-list-row-text">
                        Preço estimado
                      </span>
                      <span className="line-item-list-row-info-price">
                        {scaleAndFormatPrice(
                          lineItem.price,
                          lineItem.variant.unitForQuantity,
                        )}
                      </span>
                    </div>
                    <div className="line-item-list-row">
                      <span className="line-item-list-row-text">
                        {currentBlend
                          ? 'Nível de gordura (Blend)'
                          : 'Nível de gordura'}
                      </span>
                      <span className="line-item-list-row-info-price">
                        {currentBlend
                          ? blendFatLevel || 'Padrão'
                          : fatLevels[lineItem.fatLevel] || 'Padrão'}
                      </span>
                    </div>
                    <div className="line-item-list-row">
                      <span className="line-item-list-row-text">
                        Embalagem à Vácuo
                      </span>
                      <span className="line-item-list-row-info-price">
                        {lineItem.packagingName === 'vacuum' ? 'Sim' : 'Não'}
                      </span>
                    </div>
                    <div className="line-item-list-row">
                      <span className="line-item-list-row-text">
                        Porcionamento
                      </span>
                      <span className="line-item-list-row-info-price">
                        {lineItem.portioningSize &&
                        lineItem.portioningSize !== lineItem.quantity
                          ? 'Sim'
                          : 'Não'}
                      </span>
                    </div>
                    {lineItemPortioningMessage(lineItem)}
                  </>
                )}
                {lineItem.observation && (
                  <>
                    <button
                      className="line-item-list-button"
                      onClick={handleToggleShowObservation}
                    >
                      Observação
                      <i
                        class={`fa fa-chevron-${showObservation ? 'up' : 'down'}`}
                      ></i>
                    </button>
                    {showObservation && (
                      <textarea
                        disabled
                        contentEditable={false}
                        value={lineItem.observation}
                        className="line-item-list-observation"
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
    </section>
  );
};

export default blendsListConnector(LineItemList);
